import React, { useEffect, useState } from "react";
import { LuSparkles } from "react-icons/lu";
import { useFlowManager } from "../../../hooks/useFlowManager";
import { useNavigate } from "react-router-dom";

const ChoosePlanPage = () => {
  const plans = {
    Starter: {
      name: "Starter",
      price: "₹0",
      listingLimit: 1,
      visibilityDays: 15,
      color: "#0CC763",
      desc: [
        "1 Property Listing",
        "Visibility: 15 Days",
        "Basic Exposure",
        "No Boosting",
      ],
    },
    "Pro Agent": {
      name: "Pro Agent",
      price: "₹799",
      listingLimit: 15,
      visibilityDays: 45,
      color: "#735DFF",
      desc: [
        "15 Property Listings",
        "Visibility: 45 Days",
        "Priority Placement",
        "Agent Analytics",
      ],
    },
    "Elite Realty": {
      name: "Elite Realty",
      price: "₹1499",
      listingLimit: "Unlimited",
      visibilityDays: 90,
      color: "#FF5A29",
      desc: [
        "Unlimited Listings",
        "Visibility: 90 Days",
        "Top Tier Placement",
        "Lead Booster Access",
      ],
    },
  };

  const PRIMARY = "#EB2400";
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const payload = localStorage.getItem("payloadDraft");
    if (!payload) navigate("/post-property", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const { handlePlanSelection } = useFlowManager();

  const handleSumbit = () => {
    handlePlanSelection(selectedPlan);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto text-center mb-10">
        <div className="flex justify-center mb-4">
          <LuSparkles
            className="w-12 h-12 drop-shadow-md"
            style={{ color: PRIMARY }}
          />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-3">
          Choose Your Posting Plan
        </h1>

        <p className="text-gray-600 max-w-lg mx-auto">
          Select a plan to publish your property. The Starter plan is free and
          perfect for quick listings — upgrade anytime as you scale.
        </p>
      </div>

      {/* ------------------------------------
          PLAN CARDS
      -------------------------------------- */}
      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* STARTER PLAN */}
        <div
          className={`relative rounded-2xl border shadow-sm bg-white p-6 transition-all ${
            selectedPlan === "Starter" ? "ring-2" : "hover:shadow-lg"
          }`}
          style={{
            ringColor: PRIMARY,
            boxShadow: selectedPlan === "Starter" ? `0 0 0 2px ${PRIMARY}` : "",
          }}
        >
          <h3 className="text-xl font-bold text-gray-900">Starter Plan</h3>
          <p className="text-gray-500 text-sm mb-4">(Free)</p>

          <p className="text-3xl font-bold text-gray-900 mb-4">₹0</p>

          <ul className="space-y-2 text-gray-700 mb-6">
            {plans["Starter"].desc.map((item, idx) => (
              <li key={idx}>• {item}</li>
            ))}
          </ul>

          <button
            onClick={() => setSelectedPlan("Starter")}
            className={`w-full py-3 rounded-lg font-semibold transition ${
              selectedPlan === "Starter"
                ? "text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
            style={{
              backgroundColor: selectedPlan === "Starter" ? PRIMARY : undefined,
            }}
          >
            {selectedPlan === "Starter" ? "Selected" : "Choose Starter"}
          </button>
        </div>

        {/* PRO AGENT */}
        <div
          className={`relative rounded-2xl border shadow-sm bg-gradient-to-br from-blue-50 to-purple-50 p-6 transition-all ${
            selectedPlan === "Pro Agent" ? "ring-2" : "hover:shadow-lg"
          }`}
          style={{
            ringColor: PRIMARY,
            boxShadow:
              selectedPlan === "Pro Agent" ? `0 0 0 2px ${PRIMARY}` : "",
          }}
        >
          <h3 className="text-xl font-bold text-gray-900">Pro Agent</h3>
          <p className="text-gray-600 text-sm mb-4">Most Popular</p>

          <p className="text-3xl font-bold text-gray-900 mb-4">₹799</p>

          <ul className="space-y-2 text-gray-700 mb-6">
            {plans["Pro Agent"].desc.map((item, idx) => (
              <li key={idx}>• {item}</li>
            ))}
          </ul>

          <button
            onClick={() => setSelectedPlan("Pro Agent")}
            className={`w-full py-3 rounded-lg font-semibold transition ${
              selectedPlan === "Pro Agent"
                ? "text-white"
                : "bg-white hover:bg-gray-100 text-blue-700 border border-blue-600"
            }`}
            style={{
              backgroundColor:
                selectedPlan === "Pro Agent" ? PRIMARY : undefined,
              borderColor: selectedPlan === "Pro Agent" ? PRIMARY : undefined,
              color: selectedPlan === "Pro Agent" ? "white" : undefined,
            }}
          >
            {selectedPlan === "Pro Agent" ? "Selected" : "Choose Pro Agent"}
          </button>
        </div>

        {/* ELITE REALTY */}
        <div
          className={`relative rounded-2xl border shadow-sm bg-gradient-to-br from-pink-50 to-purple-50 p-6 transition-all ${
            selectedPlan === "Elite Realty" ? "ring-2" : "hover:shadow-lg"
          }`}
          style={{
            ringColor: PRIMARY,
            boxShadow:
              selectedPlan === "Elite Realty" ? `0 0 0 2px ${PRIMARY}` : "",
          }}
        >
          <h3 className="text-xl font-bold text-gray-900">Elite Realty</h3>
          <p className="text-gray-600 text-sm mb-4">Premium Experience</p>

          <p className="text-3xl font-bold text-gray-900 mb-4">₹1499</p>

          <ul className="space-y-2 text-gray-700 mb-6">
            {plans["Elite Realty"].desc.map((item, idx) => (
              <li key={idx}>• {item}</li>
            ))}
          </ul>

          <button
            onClick={() => setSelectedPlan("Elite Realty")}
            className={`w-full py-3 rounded-lg font-semibold transition ${
              selectedPlan === "Elite Realty"
                ? "text-white"
                : "bg-white hover:bg-gray-100 text-pink-600 border border-pink-600"
            }`}
            style={{
              backgroundColor:
                selectedPlan === "Elite Realty" ? PRIMARY : undefined,
              borderColor:
                selectedPlan === "Elite Realty" ? PRIMARY : undefined,
              color: selectedPlan === "Elite Realty" ? "white" : undefined,
            }}
          >
            {selectedPlan === "Elite Realty" ? "Selected" : "Choose Elite"}
          </button>
        </div>
      </div>

      {/* ------------------------------------
          BOTTOM ACTION BUTTON
      -------------------------------------- */}
      <div className="max-w-xl mx-auto mt-10 text-center">
        <button
          onClick={handleSumbit}
          disabled={!selectedPlan}
          className={`
    flex-1 px-6 py-3 border-2 rounded-full font-medium 
    active:scale-[0.98] transition-all duration-200 w-full
    ${
      selectedPlan
        ? "border-[#EB2400] text-[#EB2400] hover:bg-[#EB2400]/5"
        : "border-gray-300 text-gray-400 cursor-not-allowed"
    }
  `}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default ChoosePlanPage;
